import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import {
  isFunction,
  get,
  merge,
  omit,
  uniq,
  upperCase,
  remove,
  isEmpty,
  set,
  toLower,
} from 'lodash';

import { IErrorOverride } from '@app/shared/types/entities/base/base.type';
import {
  Observable,
  observableThrowError,
  rx,
} from '@app/shared/rx/rx-wrapper';
import { createParams } from '@app/shared/common/http/params';
import {
  EHeaderName,
  ICrowdinConfig,
  Primitive,
  SupportedAppCodes,
} from '@app/shared/types';

import { HttpErrorHandler } from '../base';
import { environment } from '../../../../environments/environment';
import { StateService, STATE_SELECTORS } from '../state-service';
import { ANY_TOKEN, DEFAULT_LOCALE } from '@config/global-constants';
import { lazyInject } from '@app/containers/base/decorators';
import { XVII18nService } from '@app/i18n/services';
import { ILocales } from '@app/i18n/i18n.types';

@Injectable()
export class TranslationService {
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public loadedLocales: string[] = [];

  private get baseUrl(): string {
    return environment.dmBackendURI;
  }

  public constructor(
    private http: HttpClient,
    public injector: Injector,
    public stateService: StateService,
  ) {}

  /* eslint-disable  */
  protected catchHttpError =
    (handleBeforeThrowError?: () => void, errorOverride?: IErrorOverride) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError((httpError: HttpErrorResponse) => {
          if (isFunction(handleBeforeThrowError)) {
            handleBeforeThrowError();
          }

          return observableThrowError(
            HttpErrorHandler.composeError(httpError, {
              errorOverride,
              i18nService: this.i18nService,
            }),
          );
        }),
      );

  protected extractVersionNumber(version: string): string {
    if (!version) return version;

    // Remove BUILD- prefix if exists
    let cleanVersion = version.replace('BUILD-', '');

    // Extract version number from git tag format (e.g., 3.10.233-19-g68f035a -> 3.10.233)
    // Match semantic version pattern at the beginning of the string
    const versionMatch = cleanVersion.match(/^(\d+\.\d+\.\d+)/);

    return versionMatch ? versionMatch[1] : cleanVersion;
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public getAppInfo() {
    const appName = get(environment, 'CROWDIN_APP_NAME', SupportedAppCodes.XVI);
    const appVersion = get(environment, 'version');

    return {
      appName: upperCase(appName),
      // use this to remove BUILD- prefix if exists(local or branch build)
      // and extract version from git tag format like 3.10.233-19-g68f035a
      appVersion: this.extractVersionNumber(appVersion),
    };
  }

  public getLocals(): ILocales {
    return this.stateService.getDictionaryLocals();
  }

  public getDictionaryParams(params?: {
    locale?: string;
    skipFetchingDefaultLanguage?: boolean;
    skipCustomCompanyLanguage?: boolean;
    skipUntranslatedStrings?: boolean;
  }) {
    const defaultParams = {
      locale: DEFAULT_LOCALE,
      skipFetchingDefaultLanguage: true,
      skipCustomCompanyLanguage: false,
      skipUntranslatedStrings: true,
    };

    const dictionaryParams = merge({}, defaultParams, params);

    const crowdinConfig: ICrowdinConfig = this.stateService.getByPath(
      STATE_SELECTORS.XDM_CROWDIN_CUSTOM,
    );

    let customFileName: string;

    if (
      !isEmpty(crowdinConfig?.crowdin_language_code) &&
      !dictionaryParams.skipCustomCompanyLanguage
    ) {
      customFileName = toLower(
        `${crowdinConfig.crowdin_language_code}.${dictionaryParams.locale}.json`,
      );

      // skip untranslated strings only when we have a custom company language
      set(dictionaryParams, 'skipUntranslatedStrings', false);
    } else {
      customFileName = toLower(`default.en-us.json`);
    }

    set(dictionaryParams, 'customFileName', customFileName);

    return dictionaryParams;
  }

  public getDictionaryInfo() {
    const { companyLocale, userLocale } = this.getLocals();
    const locales = [userLocale];
    if (userLocale !== companyLocale) locales.push(companyLocale);

    return { ...this.getAppInfo(), locales: uniq(locales) };
  }

  public getTranslationDictionary(userParams?: {
    locale?: string;
    appName?: string;
    appVersion?: string;
    skipFetchingDefaultLanguage?: boolean;
    skipCustomCompanyLanguage?: boolean;
    skipUntranslatedStrings?: boolean;
  }) {
    const dictionaryParams = this.getDictionaryParams(
      omit(userParams, ['appVersion', 'appName']),
    );

    const defaultAppInfo = this.getAppInfo();

    const appName = get(userParams, 'appName', defaultAppInfo.appName);
    const appVersion = get(userParams, 'appVersion', defaultAppInfo.appVersion);

    const headers = new HttpHeaders({
      [EHeaderName.Authorization]: ANY_TOKEN,
    });

    const params = createParams({
      ...dictionaryParams,
    });

    this.loadedLocales.push(dictionaryParams.locale);

    return this.http
      .get<Record<string, Primitive | Object>>(
        `${this.baseUrl}/dictionary/${appName}/build/${appVersion}`,
        {
          headers,
          params,
        },
      )
      .pipe(
        this.catchHttpError(() => {
          remove(
            this.loadedLocales,
            (locale) => locale === dictionaryParams.locale,
          );
        }),
      );
  }
}
